/*
 * Industry content — `en` (international market, source locale).
 *
 * This is the international variant of every industry (the old per-field `*Intl`
 * copy, resolved): generic time-registration framing, no belgium-specific pains/
 * features, and `compliance.slug` / `relatedArticles` pointing only at articles
 * that exist under `/en/` (the 4 market-agnostic posts). `nl.ts` carries the
 * belgium variant. Resolved by `industriesFor("en")` (also the SOURCE_LOCALE
 * fallback). Pure data (NFR10).
 */
import type { Industry } from "./types";

export const INDUSTRIES_PL: Industry[] = [
  {
    slug: "construction",
    name: "Budownictwo",
    headline: "Rejestracja czasu pracy dla zespołów budowlanych — zameldowanie z weryfikacją lokalizacji, godziny projektowe i kontrola budżetu, w jednej aplikacji",
    intro: "Ekipy rozproszone po lokalizacjach, godziny gryzmolone na papierze, godziny podwykonawców niemożliwe do uzgodnienia. Done-it daje każdej lokalizacji niepodważalną cyfrową kartę pracy, która zasila Twoje płace jednym kliknięciem.",
    pains: [
      {
        title: "Brak rzetelnego zapisu, kto i gdzie pracował",
        body: "Przy wielu aktywnych lokalizacjach i rotujących ekipach ręczne karty pracy są z założenia błędne. Pracownicy zaokrąglają w górę, zapominają o wpisach lub uzupełniają je wiele dni później — pozostawiając Cię z danymi płacowymi, których nie da się obronić.",
      },
      {
        title: "Przekroczenia budżetu wychodzą na jaw za późno",
        body: "Zanim zauważysz, że lokalizacja wyczerpała zaplanowane godziny, marża już zniknęła. Nie ma odliczania na żywo — tylko nieprzyjemna niespodzianka w chwili wystawiania faktury.",
      },
      {
        title: "Przygotowanie płac pochłania godziny każdego miesiąca",
        body: "Zbieranie kart pracy, uzgadnianie ich z projektami i przygotowywanie eksportu do płac trwa wiele dni. Każdy wpis to potencjalny błąd.",
      },
    ],
    features: [
      {
        title: "Zameldowanie z weryfikacją lokalizacji na każdej lokalizacji",
        body: "Jedno dotknięcie melduje pracownika, z weryfikacją lokalizacji, i wiąże godziny z właściwym projektem. Bez czytników kart, bez terminali — wystarczy smartfon, który Twoja ekipa już ma przy sobie.",
      },
      {
        title: "Pozostałe godziny na żywo w podziale na projekt",
        body: "Ustaw planowane godziny jako budżet przy otwieraniu projektu. Pracownicy widzą odliczanie tego, co pozostało; Ty widzisz to na żywo na swoim pulpicie. Przekroczenia budżetu są widoczne, zanim nastąpią.",
      },
      {
        title: "Zarządzanie podwykonawcami i obecnością",
        body: "Dodaj podwykonawców do projektu, przypisz ich do meldowania i śledź ich godziny obok własnej ekipy — w tym samym zestawieniu.",
      },
      {
        title: "Eksport do płac jednym kliknięciem",
        body: "Wszystkie zarejestrowane godziny — w podziale na pracownika i projekt — eksportowane do przejrzystego arkusza kalkulacyjnego dla Twojego dostawcy usług płacowych. Bez ręcznego przygotowania, bez błędów.",
      },
    ],
    compliance: {
      label: "Cyfrowa rejestracja czasu pracy",
      title: "Zameldowanie z weryfikacją lokalizacji stworzone dla placów budowy",
      body: "Done-it zapewnia zameldowanie z weryfikacją lokalizacji, rejestrację czasu w podziale na projekt i przejrzysty eksport do płac dla zespołów budowlanych. Dla zespołów działających na belgijskich lokalizacjach automatycznie zasila również check-in at work.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Ekipa przybywa na lokalizację", body: "Każdy pracownik dotyka raz, aby się zameldować. GPS potwierdza lokalizację, opatruje wpis znacznikiem czasu i wiąże godziny z projektem. Bez papieru, bez terminala." },
      { step: "Widzisz wszystko na żywo", body: "Godziny, pozostały budżet, obecność podwykonawców i wszelkie prace dodatkowe zgłoszone z lokalizacji — wszystko na Twoim pulpicie, w czasie rzeczywistym." },
      { step: "Koniec miesiąca w kilka minut", body: "Wyeksportuj wszystkie godziny w podziale na pracownika i projekt. Wyślij do swojego dostawcy usług płacowych. Done-it automatycznie generuje przejrzyste, gotowe do audytu raporty." },
    ],
    stats: [
      { value: "1 dotknięcie", label: "aby zameldować się na dowolnej lokalizacji — bez terminala, bez papieru" },
      { value: "1 kliknięcie", label: "eksport do płac gotowy dla Twojego dostawcy" },
      { value: "Na żywo", label: "widok pozostałych godzin w podziale na projekt i lokalizację" },
    ],
    quote: {
      text: "Nasza ekipa melduje się sama na lokalizacji. Na koniec miesiąca płace po prostu się zgadzają — całkowicie przestaliśmy gonić za kartami pracy.",
      who: "Właściciel, firma budowlana · 14 pracowników",
      result: "5 godzin tygodniowo zaoszczędzone na biurokracji",
    },
    relatedSlugs: ["installation", "maintenance", "field-service"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },

  {
    slug: "cleaning",
    name: "Sprzątanie",
    headline: "Rejestracja czasu pracy dla firm sprzątających — zameldowanie z weryfikacją lokalizacji, dowód obecności i płace w jednej aplikacji",
    intro: "Osoby sprzątające przemieszczają się między lokalizacjami przez cały dzień. Done-it śledzi, kto i gdzie jest, w czasie rzeczywistym — zameldowanie z weryfikacją lokalizacji na każdej lokalizacji, automatyczne odejmowanie przerw i przejrzysty eksport do płac na koniec miesiąca.",
    pains: [
      {
        title: "Udowadnianie obecności na każdej lokalizacji",
        body: "Klienci i inspektorzy oczekują niepodważalnych zapisów obecności. Ręczne arkusze można zakwestionować. Każdy wpis jest weryfikowany lokalizacyjnie.",
      },
      {
        title: "Zespoły rozproszone po wielu lokalizacjach",
        body: "Przy osobach sprzątających przemieszczających się między lokalizacjami w ciągu dnia nie masz widoku na żywo, kto i gdzie pracuje — dopóki ktoś nie zadzwoni.",
      },
      {
        title: "Błędy płacowe wynikające z zaokrąglonych godzin",
        body: "Bez dokładnych godzin wejścia/wyjścia płace są odtwarzane z pamięci. Godziny są zaokrąglane w górę, koszty rosną, a spory są nieuniknione.",
      },
    ],
    features: [
      {
        title: "Meldowanie i wymeldowanie w czasie rzeczywistym",
        body: "Pracownicy meldują się i wymeldowują na telefonie na każdej lokalizacji, z weryfikacją lokalizacji; czas jest dokładny, z rejestracją przerw.",
        plan: "Time Basic",
      },
      {
        title: "Automatyczne odejmowanie przerw",
        body: "Obowiązkowe przerwy są automatycznie odejmowane od dnia pracy — bez ręcznych obliczeń, bez sporów.",
        plan: "Time Basic",
      },
      {
        title: "Przegląd na żywo wielu lokalizacji",
        body: "Zobacz, które osoby sprzątające są na których lokalizacjach w danej chwili. Wyłap luki, zanim zrobią to klienci.",
      },
      {
        title: "Rejestracja nieobecności z cyfrowymi zwolnieniami lekarskimi",
        body: "Pracownicy przesyłają zaświadczenia lekarskie i dokumenty nieobecności bezpośrednio z aplikacji. Utwórz rodzaje nieobecności, których Twój zespół faktycznie używa.",
      },
      {
        title: "Eksport do płac jednym kliknięciem",
        body: "Dokładne znaczniki czasu trafiają prosto do eksportu gotowego do rozliczenia płac dla Twojego dostawcy. Bez przepisywania, bez błędów.",
      },
    ],
    compliance: {
      label: "Obecność z weryfikacją lokalizacji",
      title: "Obecność w czasie rzeczywistym na każdej lokalizacji sprzątania",
      body: "Done-it daje zespołom sprzątającym zameldowanie z weryfikacją lokalizacji na każdej lokalizacji, automatyczne odejmowanie przerw i widok na żywo, kto jest na której lokalizacji w danej chwili.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Osoba sprzątająca przybywa na lokalizację", body: "Jedno dotknięcie ją melduje. GPS rejestruje lokalizację i opatruje wpis znacznikiem czasu. Rejestracja przerw jest wbudowana w aplikację." },
      { step: "Widzisz każdą lokalizację na żywo", body: "Kto jest na której lokalizacji w danej chwili — bez telefonów, bez zgadywania. Wyłap luki w obsadzie, zanim zrobią to klienci." },
      { step: "Płace jednym kliknięciem", body: "Wszystkie dane o obecności — dokładne godziny, lokalizacje i przerwy — eksportowane w przejrzystej formie dla Twojego dostawcy usług płacowych." },
    ],
    stats: [
      { value: "1 dotknięcie", label: "aby zameldować się na dowolnej lokalizacji — bez potrzeby terminala" },
      { value: "Na żywo", label: "widok, kto jest na której lokalizacji w danej chwili" },
      { value: "0", label: "podwójnych wpisów — godziny trafiają prosto do płac" },
    ],
    quote: {
      text: "W końcu możemy udowodnić, kto sprzątał, gdzie i kiedy. Koniec z arkuszami obecności, koniec ze sporami z klientami — GPS zajmuje się tym za nas.",
      who: "Menedżer, firma sprzątająca · 9 pracowników",
      result: "Zero zaokrąglania godzin w górę",
    },
    relatedSlugs: ["maintenance", "field-service", "construction"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },

  {
    slug: "installation",
    name: "Instalacje",
    headline: "Rejestracja czasu pracy dla zespołów instalacyjnych — godziny projektowe, nawigacja i kontrola budżetu w jednej aplikacji",
    intro: "Wyślij swoich monterów pod właściwy adres ze wszystkim, czego potrzebują, powiąż każdą przepracowaną godzinę z właściwym zleceniem automatycznie i zobacz pozostały budżet, zanim lokalizacja zostanie zamknięta. Done-it to najprostszy sposób na utrzymanie projektów instalacyjnych w terminie i w ramach kosztów.",
    pains: [
      {
        title: "Informacje o zleceniu nie docierają do terenu na czas",
        body: "Ekipy przybywają na lokalizację bez adresu, osoby kontaktowej czy szczegółów zakresu. Ten pierwszy telefon to dziesięć minut godzin podlegających rozliczeniu, straconych, zanim przepracowano choćby jedną godzinę.",
      },
      {
        title: "Godziny nie są powiązane z właściwym projektem",
        body: "Gdy pracownicy uzupełniają swoje godziny ręcznie na koniec dnia — o ile w ogóle je uzupełniają — godziny trafiają do niewłaściwego zlecenia lub znikają całkowicie. Faktury nie zgadzają się z rzeczywistością.",
      },
      {
        title: "Przekroczenia budżetu zauważane za późno",
        body: "Bez odliczania na żywo pozostałych godzin w podziale na projekt dowiadujesz się, że zlecenie przekroczyło budżet, gdy zamykasz fakturę — a nie wtedy, gdy jeszcze możesz zareagować.",
      },
      {
        title: "Godziny podwykonawców trudne do śledzenia",
        body: "Podwykonawcy pracujący na tej samej lokalizacji korzystają z różnych systemów, uzupełniają godziny osobno lub wcale. Uzgadnianie ich na koniec miesiąca to powolny, podatny na błędy proces.",
      },
    ],
    features: [
      {
        title: "Informacje o projekcie udostępniane bezpośrednio do aplikacji",
        body: "Pracownicy otwierają projekt na telefonie: adres, planowane godziny, osoba kontaktowa i przesłane pliki (plany, instrukcje, pliki PDF). Wszystko, czego potrzebują, jest tam, zanim przybędą.",
        plan: "Project Basic",
      },
      {
        title: "Nawigacja do lokalizacji jednym przyciskiem",
        body: "Dotknij raz, a telefon nawiguje do adresu projektu — wbudowane w aplikację, bez potrzeby osobnej aplikacji z mapami.",
      },
      {
        title: "Odliczanie pozostałych godzin w podziale na projekt",
        body: "Ustaw planowane godziny przy otwieraniu projektu. Pracownicy widzą, ile godzin pozostało; Ty widzisz status na żywo na swoim pulpicie. Przekroczenia są widoczne, zanim nastąpią.",
        plan: "Project Basic",
      },
      {
        title: "Zameldowanie pracownika w podziale na projekt",
        body: "Każdy pracownik melduje się w odpowiednim projekcie. Zawsze wiesz, kto jest na której lokalizacji w danej chwili.",
        plan: "Project Basic",
      },
      {
        title: "Rejestracja prac dodatkowych i materiałów",
        body: "Gdy zakres rozrasta się na lokalizacji, pracownicy rejestrują prace dodatkowe i materiały w czasie rzeczywistym — z tekstem i zdjęciami — więc nic nie jest rozliczane z pamięci.",
        plan: "Project Basic",
      },
    ],
    compliance: {
      label: "Cyfrowa rejestracja czasu pracy",
      title: "Zameldowanie z weryfikacją lokalizacji w podziale na projekt dla zespołów instalacyjnych",
      body: "Done-it daje zespołom instalacyjnym zameldowanie z weryfikacją lokalizacji w podziale na projekt, odliczanie pozostałych godzin na żywo i automatyczny eksport do płac — pokrywając każdy lokalny wymóg rejestracji czasu pracy. Dla zespołów pracujących na belgijskich placach budowy integruje się również z CheckIn@Work.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Pracownik otwiera projekt na telefonie", body: "Adres, kontakt, zakres i przesłane pliki są od razu pod ręką. Nawiguje jednym dotknięciem — bez telefonów do biura po wskazówki." },
      { step: "Godziny rejestrują się automatycznie do właściwego zlecenia", body: "Zameldowanie wiąże każdą przepracowaną minutę z właściwym projektem. Pozostały budżet aktualizuje się w czasie rzeczywistym." },
      { step: "Prace dodatkowe rejestrowane na lokalizacji", body: "Zmiany zakresu, dodatkowe materiały i opóźnienia są rejestrowane z tekstem i zdjęciami w chwili, gdy się dzieją — gotowe na fakturę." },
    ],
    stats: [
      { value: "W czasie rzeczywistym", label: "widok pozostałych godzin w podziale na projekt" },
      { value: "0", label: "telefonów do biura po adresy lokalizacji" },
      { value: "1 kliknięcie", label: "nawigacja do dowolnego adresu projektu z aplikacji" },
    ],
    quote: {
      text: "Godziny trafiają do właściwego projektu automatycznie. Kosztorysowanie zleceń stało się nagle proste — widzimy rzeczywistą marżę, zanim zamkniemy zlecenie.",
      who: "Właściciel, firma instalacyjna · 6 pracowników",
      result: "Rzeczywisty koszt zlecenia, na żywo",
    },
    relatedSlugs: ["construction", "electricians", "hvac"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },

  {
    slug: "field-service",
    name: "Serwis terenowy",
    headline: "Rejestracja czasu pracy dla zespołów serwisu terenowego — przebieg, wydatki i godziny z drogi, wszystko w jednej aplikacji",
    intro: "Technicy odwiedzający wielu klientów dziennie, paragony w schowkach, przebieg rejestrowany z pamięci. Done-it rejestruje godziny, kilometry i wydatki w terenie — przypisane do właściwego zlecenia w chwili, gdy się dzieją.",
    pains: [
      {
        title: "Przebieg rejestrowany wiele dni później — o ile w ogóle",
        body: "Proszenie techników o odtworzenie tras na koniec tygodnia oznacza niedokładne zwroty kosztów, spory z klientami i rosnący stos nierozliczonych kilometrów.",
      },
      {
        title: "Wydatki na papierowych paragonach, które znikają",
        body: "Paragon w schowku to koszt, który być może nigdy nie trafi na fakturę. Rzeczywisty koszt zlecenia jest zawsze zaniżony.",
      },
      {
        title: "Brak widoku w czasie rzeczywistym, gdzie jest zespół",
        body: "Bez zameldowań na żywo dowiadujesz się, gdzie jest technik, tylko wtedy, gdy coś pójdzie nie tak i ktoś zadzwoni.",
      },
      {
        title: "Trudno zobaczyć rzeczywistą marżę zlecenia",
        body: "Gdy robocizna, dojazd i wydatki są śledzone w osobnych systemach — albo wcale — nigdy nie ma jednej kwoty kosztu zlecenia.",
      },
    ],
    features: [
      {
        title: "Automatyczna rejestracja przebiegu (Trax-it)",
        body: "GPS rejestruje wyjazd, przybycie i dokładną odległość każdego przejazdu. Skonfiguruj rodzaje transportu (własny samochód, pojazd służbowy, wspólny dojazd) z ich własnymi stawkami zwrotu. Bez ręcznego rejestru.",
        plan: "Dodatek",
      },
      {
        title: "Zarządzanie wydatkami na lokalizacji",
        body: "Pracownicy robią zdjęcie paragonu w terenie; jest on przypisywany do zlecenia i gotowy do zwrotu — a nie ścigany na koniec miesiąca.",
        plan: "Time Basic",
      },
      {
        title: "Kontrola kosztów powiązana z projektem",
        body: "Godziny, przebieg i wydatki sumują się w ramach zlecenia w czasie rzeczywistym. Koszt zlecenia jest widoczny, gdy zlecenie jest jeszcze otwarte.",
        plan: "Project Basic",
      },
      {
        title: "Zameldowanie z weryfikacją lokalizacji i przegląd zespołu na żywo",
        body: "Każdy technik melduje się na lokalizacji klienta. Widzisz, kto i gdzie jest w danej chwili — bez telefonów, bez zgadywania.",
      },
      {
        title: "Eksport do płac jednym kliknięciem",
        body: "Godziny i zwroty kosztów kilometrów eksportowane w przejrzystej formie dla Twojego dostawcy usług płacowych — z automatycznie zastosowanymi skonfigurowanymi stawkami przebiegu.",
      },
    ],
    compliance: {
      label: "Cyfrowa rejestracja czasu i przebiegu",
      title: "Godziny z GPS i automatyczny przebieg dla zespołów serwisu terenowego",
      body: "Done-it rejestruje przebieg automatycznie dla każdego przejazdu, stosuje skonfigurowane stawki zwrotu i przypisuje każdy kilometr do właściwego klienta lub projektu. Unijny wyrok w sprawie Tyco oznacza, że dojazd z domu do pierwszego klienta liczy się jako czas pracy — Done-it śledzi również to.",
      slug: "how-to-track-mileage-for-field-teams",
    },
    workflow: [
      { step: "Technik wyrusza na pierwsze wezwanie", body: "Zameldowanie rozpoczyna rejestr przejazdu. Kilometry są rejestrowane automatycznie od wyjazdu do przybycia — bez ręcznego wprowadzania." },
      { step: "Na lokalizacji zlecenia", body: "Godziny wiążą się z właściwym projektem. Prace dodatkowe, materiały i paragony są rejestrowane w aplikacji w chwili, gdy się dzieją." },
      { step: "Koniec miesiąca bez chaosu", body: "Każda godzina, kilometr i wydatek są już przypisane do właściwego zlecenia. Eksportuj do płac i fakturuj z rzeczywistymi danymi kosztowymi." },
    ],
    stats: [
      { value: "Automatycznie", label: "rejestracja przebiegu — bez ręcznego rejestru" },
      { value: "Na żywo", label: "koszt zlecenia: godziny + dojazd + wydatki" },
      { value: "0", label: "utraconych kilometrów — każdy przejazd rejestrowany automatycznie" },
    ],
    quote: {
      text: "Przebieg był kiedyś comiesięcznym bólem głowy. Teraz rejestruje się sam — każdy kilometr jest przypisany do właściwego klienta, zanim w ogóle wrócimy do biura.",
      who: "Kierownik operacyjny, firma serwisu terenowego · 11 pracowników",
      result: "Dokładny koszt zlecenia, za każdym razem",
    },
    relatedSlugs: ["hvac", "maintenance", "plumbing"],
    relatedArticles: [
      "how-to-track-mileage-for-field-teams",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },

  {
    slug: "hvac",
    name: "HVAC",
    headline: "Rejestracja czasu pracy dla techników HVAC — kosztorysowanie zleceń, części i dojazd w jednej aplikacji",
    intro: "Każde wezwanie serwisowe ma trzy koszty: robociznę, części i dojazd. Done-it rejestruje wszystkie trzy w terenie i sumuje je w ramach właściwego zlecenia — dzięki czemu znasz swoją marżę, zanim zamkniesz fakturę.",
    pains: [
      {
        title: "Wezwania serwisowe trudno dokładnie wycenić",
        body: "Robocizna, części i dojazd żyją w różnych systemach — lub różnych kieszeniach. Rzeczywisty koszt wezwania staje się jasny dopiero długo po tym, jak technik opuści lokalizację.",
      },
      {
        title: "Brak dowodu czasu serwisu",
        body: "Gdy klient kwestionuje, jak długo trwała naprawa, papierową kartę pracy łatwo podważyć. Potwierdzonych przez GPS znaczników czasu zameldowania i wymeldowania — nie.",
      },
      {
        title: "Umowy serwisowe po cichu zżerają marżę",
        body: "W przypadku cyklicznych umów serwisowych drobne przekroczenia kumulują się na przestrzeni wielu wizyt. Bez licznika godzin na żywo dowiadujesz się, że umowa jest pod kreską, dopiero przy jej odnowieniu.",
      },
      {
        title: "Technicy zapominają rejestrować przerwy",
        body: "Ręczne wpisy czasu pomijają przerwy, co prowadzi do nadpłat i płac, które nie zgadzają się z rzeczywistością.",
      },
    ],
    features: [
      {
        title: "Rejestracja czasu oparta na projektach",
        body: "Każde wezwanie serwisowe lub umowa serwisowa to projekt. Godziny, dojazd i materiały sumują się w czasie rzeczywistym — dając Ci widoczność marży na żywo.",
        plan: "Project Basic",
      },
      {
        title: "Prace dodatkowe i materiały rejestrowane na lokalizacji",
        body: "Gdy zlecenie się rozrasta, technicy rejestrują prace dodatkowe i części z tekstem i zdjęciami, zanim wyjadą. Nic nie jest rozliczane z pamięci.",
        plan: "Project Basic",
      },
      {
        title: "Przebieg i koszty transportu",
        body: "Kilometry na wezwanie są rejestrowane automatycznie i przypisywane do kosztów zlecenia. Skonfiguruj stawki zwrotu dla rodzaju pojazdu.",
        plan: "Dodatek",
      },
      {
        title: "Automatyczne odejmowanie przerw",
        body: "Obowiązkowe przerwy są automatycznie odejmowane od całkowitego czasu — bez sporów, bez błędów płacowych.",
      },
      {
        title: "Zameldowanie z weryfikacją lokalizacji i dowód obecności",
        body: "Opatrzone znacznikiem czasu, potwierdzone przez GPS zameldowanie na każdej lokalizacji. Każdy wpis jest weryfikowany lokalizacyjnie.",
      },
    ],
    compliance: {
      label: "Cyfrowa rejestracja czasu i kosztów",
      title: "Pełne kosztorysowanie zleceń dla wezwań serwisowych HVAC",
      body: "Done-it daje zespołom HVAC zameldowanie z weryfikacją lokalizacji w podziale na wezwanie serwisowe, widoczność kosztów na żywo w podziale na zlecenie (robocizna + dojazd + części) i przejrzysty eksport do płac — spełniając każdy lokalny wymóg rejestracji czasu pracy bez dodatkowego sprzętu.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Technik melduje się na lokalizacji", body: "GPS opatruje przybycie znacznikiem czasu i wiąże godziny z wezwaniem serwisowym lub umową serwisową. Bez papieru, bez terminala." },
      { step: "Prace dodatkowe rejestrowane na miejscu", body: "Użyte części, dodatkowe godziny i zmiany zakresu są rejestrowane w aplikacji ze zdjęciami — przed opuszczeniem lokalizacji." },
      { step: "Koszt zlecenia widoczny natychmiast", body: "Robocizna, dojazd i materiały sumują się w ramach projektu w czasie rzeczywistym. Widzisz marżę, zanim wystawisz fakturę." },
    ],
    stats: [
      { value: "3", label: "rodzaje kosztów rejestrowane na wezwanie: robocizna, dojazd, części" },
      { value: "Na żywo", label: "widoczność marży w podziale na umowę serwisową" },
      { value: "0", label: "niespodzianek przy fakturowaniu — koszty widoczne w czasie rzeczywistym" },
    ],
    quote: {
      text: "W końcu wiemy, ile naprawdę kosztuje nas każda umowa serwisowa. Niespodzianki z marżą skończyły się w dniu, w którym zaczęliśmy używać Done-it.",
      who: "Właściciel, firma HVAC · 7 techników",
      result: "Rzeczywisty koszt wezwania serwisowego, na żywo",
    },
    relatedSlugs: ["installation", "electricians", "plumbing"],
    relatedArticles: [
      "how-to-track-mileage-for-field-teams",
      "time-tracking-for-field-teams-complete-guide",
    ],
  },

  {
    slug: "electricians",
    name: "Elektrycy",
    headline: "Rejestracja czasu pracy dla elektryków — godziny w podziale na projekt, kontrola budżetu i nawigacja w jednej aplikacji",
    intro: "Godziny w podziale na projekt, odliczanie pozostałego budżetu na żywo i nawigacja do lokalizacji jednym przyciskiem — stworzone dla małych zespołów elektryków, które potrzebują kontroli nad projektami bez nadmiaru biurokracji.",
    pains: [
      {
        title: "Godziny nie są powiązane z właściwym projektem",
        body: "Gdy elektrycy pracują przy wielu zleceniach w tygodniu, ręczne przypisywanie godzin na koniec dnia prowadzi do błędnie przypisanych kosztów, sporów dotyczących rozliczeń i płac, których nie da się wyjaśnić.",
      },
      {
        title: "Przekroczenia budżetu zauważane za późno",
        body: "Bez widoku na żywo godzin zużytych względem zaplanowanych dowiadujesz się, że projekt przekroczył budżet, gdy wystawiasz fakturę — a nie wtedy, gdy jeszcze mógłbyś zareagować.",
      },
      {
        title: "Czas tracony na znalezienie lokalizacji",
        body: "Adresy wpisywane do osobnej aplikacji, telefony do biura po wskazówki, spóźnione przyjazdy do klientów. Każda zmarnowana minuta to stracona minuta podlegająca rozliczeniu.",
      },
      {
        title: "Godziny podwykonawców trudne do uzgodnienia",
        body: "Przy większych instalacjach podwykonawcy elektrycy śledzą swoje godziny osobno. Uzyskanie połączonego widoku kosztów robocizny na koniec projektu to ręczna, podatna na błędy praca.",
      },
    ],
    features: [
      {
        title: "Godziny projektowe z pozostałym budżetem na żywo",
        body: "Każda przepracowana minuta jest automatycznie powiązana z właściwym projektem. Odliczanie na żywo pokazuje, ile godzin pozostało względem budżetu — dla Ciebie i dla pracownika na lokalizacji.",
        plan: "Project Basic",
      },
      {
        title: "Nawigacja do lokalizacji jednym przyciskiem",
        body: "Adres projektu jest w aplikacji. Jedno dotknięcie otwiera nawigację — bez szukania, bez osobnej aplikacji z mapami, bez telefonów do biura.",
      },
      {
        title: "Zameldowanie pracownika w podziale na projekt",
        body: "Każdy elektryk melduje się przy odpowiednim zleceniu. Zawsze wiesz, kto jest na której lokalizacji w danej chwili.",
        plan: "Project Basic",
      },
      {
        title: "Prace dodatkowe rejestrowane na lokalizacji",
        body: "Gdy zakres się zmienia — nieoczekiwany obwód, dodatkowe materiały — jest to rejestrowane w aplikacji ze zdjęciami, zanim ekipa wyjedzie.",
        plan: "Project Basic",
      },
      {
        title: "Rejestracja nadgodzin z elastyczną rekompensatą",
        body: "Nadgodziny są rejestrowane w chwili, gdy następują. Pracownicy wybierają wypłatę lub zamianę na czas wolny.",
        plan: "Time Pro",
      },
    ],
    compliance: {
      label: "Zameldowanie z weryfikacją lokalizacji w podziale na projekt",
      title: "Kontrola budżetu na żywo i zameldowanie z weryfikacją lokalizacji dla zespołów elektryków",
      body: "Done-it daje elektrykom zameldowanie z weryfikacją lokalizacji w podziale na projekt, budżet pozostałych godzin na żywo i eksport do płac — spełniając każdy lokalny wymóg rejestracji czasu pracy. Dla zespołów na belgijskich placach budowy integruje się również z CheckIn@Work.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Elektryk nawiguje do zlecenia", body: "Adres i zakres są już w aplikacji. Jedno dotknięcie otwiera nawigację — bez telefonów, bez zamieszania." },
      { step: "Godziny wiążą się z właściwym projektem automatycznie", body: "Zameldowanie na lokalizacji wiąże każdą przepracowaną minutę z właściwym projektem. Licznik pozostałych godzin aktualizuje się na żywo." },
      { step: "Zmiany zakresu pozostają w zapisie", body: "Prace dodatkowe i materiały są rejestrowane na lokalizacji, przed wyjazdem. Faktura odzwierciedla to, co naprawdę się wydarzyło." },
    ],
    stats: [
      { value: "Na żywo", label: "widok pozostałych godzin w podziale na projekt" },
      { value: "0", label: "telefonów do biura po adresy lokalizacji" },
      { value: "1 dotknięcie", label: "aby zameldować się na dowolnej lokalizacji — bez potrzeby terminala" },
    ],
    quote: {
      text: "W czasie rzeczywistym wiemy, czy projekt przekracza budżet. Już samo to uchroniło nas przed dwiema przykrymi niespodziankami w tym roku.",
      who: "Właściciel, firma elektryczna · 5 elektryków",
      result: "Przekroczenia budżetu wyłapywane, zanim nastąpią",
    },
    relatedSlugs: ["installation", "hvac", "plumbing"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "how-to-track-mileage-for-field-teams",
    ],
  },

  {
    slug: "plumbing",
    name: "Hydraulika",
    headline: "Rejestracja czasu pracy dla zespołów hydraulicznych — szybkie zameldowanie, dokładne płace i dowód obecności dla każdego wezwania",
    intro: "Wezwania awaryjne, wiele zleceń dziennie, godziny uzupełniane z pamięci na koniec tygodnia — albo wcale. Done-it sprawia, że na każdej lokalizacji to kwestia jednego dotknięcia, a na koniec miesiąca płace to jedno kliknięcie.",
    pains: [
      {
        title: "Godziny wezwań odtwarzane z pamięci",
        body: "Przy kilku zleceniach dziennie hydraulicy rzadko mają czas na wypełnienie dokładnych kart pracy. Godziny są zaokrąglane lub zapominane, a płace to w najlepszym razie szacunek.",
      },
      {
        title: "Brak dowodu czasu na lokalizacji",
        body: "Gdy klient kwestionuje, jak długo trwała naprawa, nie ma obiektywnego zapisu. Potwierdzone przez GPS cyfrowe zameldowanie łatwo przedstawić — papierowej karty pracy nie.",
      },
      {
        title: "Nieśledzony przebieg między zleceniami",
        body: "Dojazdy między wezwaniami sumują się w ciągu tygodnia i miesiąca, ale bez automatycznej rejestracji rzadko są poprawnie fakturowane lub zwracane.",
      },
      {
        title: "Przygotowanie płac jest powolne",
        body: "Na koniec miesiąca gonienie za kartami pracy, poprawianie błędów i budowanie eksportu do płac zajmuje czas, którego Twój zespół administracyjny nie powinien na to poświęcać.",
      },
    ],
    features: [
      {
        title: "Zameldowanie z weryfikacją lokalizacji jednym dotknięciem przy każdym wezwaniu",
        body: "Przybycie i wyjazd rejestrowane jednym dotknięciem, z weryfikacją lokalizacji. Wystarczająco szybkie dla wezwań awaryjnych; wystarczająco dokładne dla sporów.",
        plan: "Time Basic",
      },
      {
        title: "Automatyczne odejmowanie przerw",
        body: "Obowiązkowe przerwy są automatycznie odejmowane od całkowitego czasu — nic do uzupełnienia, bez błędów płacowych.",
        plan: "Time Basic",
      },
      {
        title: "Przebieg rejestrowany dla każdego przejazdu",
        body: "Skonfiguruj rodzaje transportu i stawki zwrotu. Kilometry rejestrują się automatycznie i wiążą z właściwym klientem lub projektem.",
        plan: "Dodatek",
      },
      {
        title: "Zarządzanie nieobecnościami i zwolnieniami lekarskimi",
        body: "Pracownicy przesyłają zwolnienia lekarskie cyfrowo z aplikacji. Utwórz rodzaje nieobecności, których Twój zespół faktycznie używa, w tym przestoje pogodowe.",
      },
      {
        title: "Eksport do płac jednym kliknięciem",
        body: "Wszystkie dane o godzinach i kilometrach eksportowane w przejrzystej formie dla Twojego dostawcy usług płacowych — bez ręcznego przygotowania, bez Excela.",
        plan: "Time Basic",
      },
    ],
    compliance: {
      label: "Cyfrowa rejestracja czasu pracy",
      title: "Szybkie zameldowanie z weryfikacją lokalizacji dla każdego wezwania",
      body: "Done-it pokrywa każdy lokalny wymóg rejestracji czasu pracy dla zespołów hydraulicznych — zameldowanie z weryfikacją lokalizacji jednym dotknięciem przy każdym wezwaniu, automatyczne odejmowanie przerw i przejrzysty eksport do płac. Bez papierowych kart pracy, bez ręcznego odtwarzania.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Hydraulik przybywa na wezwanie", body: "Jedno dotknięcie melduje go. GPS rejestruje lokalizację i czas. Wystarczająco szybkie dla zlecenia awaryjnego." },
      { step: "Wyjazd zaznaczany dotknięciem w drodze powrotnej", body: "Wymeldowanie zamyka wpis czasu. Przebieg do następnego zlecenia zaczyna rejestrować się automatycznie." },
      { step: "Koniec miesiąca to jedno kliknięcie", body: "Wszystkie wezwania, godziny i kilometry eksportowane w przejrzystej formie. Przygotowanie płac skraca się z dnia do minut." },
    ],
    stats: [
      { value: "1 dotknięcie", label: "aby się zameldować — wystarczająco szybkie dla wezwań awaryjnych" },
      { value: "1 kliknięcie", label: "eksport do płac do Twojego dostawcy" },
      { value: "0", label: "godzin odtwarzanych z pamięci na koniec miesiąca" },
    ],
    quote: {
      text: "Kiedyś spędzaliśmy poranek na koniec miesiąca, odtwarzając godziny. Teraz po prostu eksportujemy i wysyłamy. Dostawca usług płacowych otrzymuje je w przejrzystej formie.",
      who: "Właściciel, firma hydrauliczna · 4 hydraulików",
      result: "Przygotowanie płac skrócone z godzin do minut",
    },
    relatedSlugs: ["hvac", "electricians", "field-service"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "how-to-track-mileage-for-field-teams",
    ],
  },

  {
    slug: "landscaping",
    name: "Architektura krajobrazu",
    headline: "Rejestracja czasu pracy dla firm zajmujących się architekturą krajobrazu — ekipy sezonowe, wiele lokalizacji i przebieg w jednym zestawieniu",
    intro: "Szczyt sezonu oznacza wiele lokalizacji, zmieniające się ekipy i mnóstwo kilometrów. Done-it utrzymuje każdą godzinę, każdą nieobecność i każdy przejazd w porządku — dzięki czemu możesz skupić się na pracy, a nie na papierkowej robocie.",
    pains: [
      {
        title: "Ekipy sezonowe są złożone w zarządzaniu",
        body: "Pracownicy tymczasowi, zmieniające się grafiki i różny rozmiar zespołów sprawiają, że dokładne śledzenie godzin na papierze jest niemal niemożliwe. Zanim uzgodnisz wszystko na koniec miesiąca, zawsze czegoś brakuje.",
      },
      {
        title: "Godziny rozproszone po wielu lokalizacjach",
        body: "Ekipa odwiedzająca trzy lub cztery lokalizacje dziennie generuje godziny, które trzeba poprawnie rozdzielić. Ręczne wprowadzanie prowadzi do błędnego przypisania i kwestionowanych płac.",
      },
      {
        title: "Przestoje pogodowe trudno udokumentować",
        body: "Przestój z powodu złej pogody to realne zdarzenie w architekturze krajobrazu. Bez cyfrowego zapisu dokumentowanie przestojów i korygowanie wypłat jest powolne i podatne na błędy.",
      },
      {
        title: "Przebieg między lokalizacjami pozostaje nieśledzony",
        body: "Furgonetki firmowe, pojazdy prywatne i wspólne dojazdy krzyżują się między lokalizacjami przez cały dzień. Bez automatycznej rejestracji te kilometry nigdy nie trafiają do obrazu kosztów.",
      },
    ],
    features: [
      {
        title: "Przegląd ekip na wielu lokalizacjach",
        body: "Zobacz wszystkie ekipy, wszystkie lokalizacje w czasie rzeczywistym. Wiedz, kto i gdzie pracuje w danej chwili — a nie na koniec miesiąca.",
      },
      {
        title: "Planowanie zmian dla zespołów sezonowych",
        body: "Zaplanuj, kto i gdzie pracuje, obsłuż zmiany w ostatniej chwili i wyślij grafik na telefony zespołu — bez dzwonienia do każdej osoby.",
        plan: "Time Pro",
      },
      {
        title: "Niestandardowe rodzaje nieobecności",
        body: "Utwórz niestandardowe rodzaje nieobecności dla swojego sektora: sezonowe przestoje pogodowe, urlop rodzicielski, dni chorobowe. Pracownicy przesyłają dokumentację cyfrowo z aplikacji.",
      },
      {
        title: "Przebieg ze śledzeniem wspólnych dojazdów",
        body: "Skonfiguruj rodzaje transportu (furgonetka firmowa, samochód prywatny, kierowca a pasażer wspólnego dojazdu) z ich własnymi stawkami. Kilometry rejestrują się automatycznie dla każdego przejazdu.",
        plan: "Dodatek",
      },
      {
        title: "Rejestracja nadgodzin z zamianą na urlop",
        body: "Nadgodziny w szczycie sezonu kumulują się szybko. Każdy pracownik może wybrać wypłatę lub odłożenie godzin jako czasu wolnego — śledzone w przejrzysty sposób.",
        plan: "Time Pro",
      },
    ],
    compliance: {
      label: "Cyfrowa rejestracja czasu pracy",
      title: "Zameldowanie z weryfikacją lokalizacji i śledzenie nieobecności dla sezonowych zespołów architektury krajobrazu",
      body: "Done-it daje zespołom architektury krajobrazu cyfrową rejestrację czasu pracy na wszystkich lokalizacjach i wśród personelu sezonowego — zameldowanie z weryfikacją lokalizacji, niestandardowe rodzaje nieobecności dla przestojów pogodowych i przejrzysty eksport do płac spełniający każdy lokalny wymóg.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Ekipa melduje się na pierwszej lokalizacji", body: "GPS rejestruje przybycie. Godziny automatycznie dzielą się na właściwą lokalizację i projekt — bez ręcznego przypisywania na koniec dnia." },
      { step: "Pogoda wstrzymuje pracę", body: "Brygadzista rejestruje przestój z aplikacji — przejrzysty zapis cyfrowy, bez dodatkowej papierkowej roboty." },
      { step: "Koniec miesiąca: wszystkie lokalizacje, wszystkie ekipy, jeden eksport", body: "Godziny, nieobecności i przebieg ze wszystkich lokalizacji i od każdego członka ekipy eksportowane w przejrzystej formie do płac." },
    ],
    stats: [
      { value: "Automatycznie", label: "rejestracja przebiegu — koniec z rejestrem" },
      { value: "Na żywo", label: "przegląd ekip na wszystkich lokalizacjach" },
      { value: "1 kliknięcie", label: "eksport do płac dla wszystkich lokalizacji i wszystkich ekip" },
    ],
    quote: {
      text: "Szczyt sezonu oznaczał kiedyś chaos płacowy. Teraz nasz brygadzista sprawdza aplikację, wszyscy są rozliczeni, a ja eksportuję godziny ostatniego dnia miesiąca.",
      who: "Właściciel, firma architektury krajobrazu · 12 pracowników sezonowych",
      result: "Koniec z odtwarzaniem płac na koniec miesiąca",
    },
    relatedSlugs: ["maintenance", "construction", "field-service"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },

  {
    slug: "maintenance",
    name: "Konserwacja",
    headline: "Rejestracja czasu pracy dla zespołów konserwacyjnych — cykliczne wizyty na lokalizacjach, koszt w podziale na umowę i dowód obecności w jednej aplikacji",
    intro: "Cykliczne umowy konserwacyjne stoją lub upadają na widoczności kosztów. Done-it rejestruje każdą wizytę, śledzi godziny względem budżetu umowy i daje Tobie — oraz Twoim klientom — dowód obecności na każde wezwanie.",
    pains: [
      {
        title: "Brak widoczności kosztów w podziale na umowę konserwacyjną",
        body: "Gdy godziny cyklicznych wizyt nie są śledzone względem konkretnej umowy, nie wiesz, czy klient jest rentowny, aż do przeglądu na koniec roku — a wtedy jest już za późno na zmianę ceny.",
      },
      {
        title: "Klienci oczekują dowodu na to, co zrobiono",
        body: "Zarządcy obiektów i właściciele nieruchomości coraz częściej chcą zapisów obecności: kto przyszedł, kiedy przybył, co zrobił. Papierowy rejestr przedstawiony wiele tygodni później nie spełnia wymogów SLA.",
      },
      {
        title: "Cykliczne wizyty trudno zaplanować i zarejestrować",
        body: "Rotujące zmiany na wielu cyklicznych lokalizacjach — z personelem, który się wzajemnie zastępuje — tworzą zagadkę planowania i śledzenia godzin, której procesy ręczne nie potrafią rzetelnie rozwiązać.",
      },
      {
        title: "Przestoje pogodowe dotyczą również konserwacji na zewnątrz",
        body: "Utrzymanie terenów zielonych, konserwacja elewacji i prace przy obiektach zewnętrznych — wszystkie mierzą się z przestojami pogodowymi. Poprawne ich udokumentowanie wymaga cyfrowego śladu.",
      },
    ],
    features: [
      {
        title: "Śledzenie kosztów oparte na umowach",
        body: "Każda umowa konserwacyjna to projekt. Godziny rejestrują się względem niej automatycznie, dając Ci kwotę kosztu na żywo i odliczanie pozostałych godzin dla każdego klienta.",
        plan: "Project Basic",
      },
      {
        title: "Dowód obecności potwierdzony przez GPS",
        body: "Opatrzone znacznikiem czasu, zweryfikowane lokalizacyjnie zameldowania są zapisem, kto był na lokalizacji, kiedy i jak długo — gotowym do udostępnienia klientom na żądanie.",
      },
      {
        title: "Aktualizacje ze zdjęciami z lokalizacji",
        body: "Pracownicy zgłaszają problemy, ukończone zadania lub prace dodatkowe bezpośrednio z aplikacji — z tekstem i zdjęciami. Klienci otrzymują zapis w czasie rzeczywistym; Ty masz dokumentację do rozliczeń.",
        plan: "Project Basic",
      },
      {
        title: "Planowanie cyklicznych zmian",
        body: "Zaplanuj cykliczne wizyty na wszystkich umowach, przypisz pracowników i obsłuż zastępstwa — bez dzwonienia do każdej osoby.",
        plan: "Time Pro",
      },
      {
        title: "Rodzaje nieobecności dla konserwacji na zewnątrz",
        body: "Skonfiguruj przestoje pogodowe i inne rodzaje nieobecności. Pracownicy rejestrują je z aplikacji; dokumentacja przesyłana jest cyfrowo.",
      },
    ],
    compliance: {
      label: "Śledzenie czasu oparte na umowach",
      title: "Widoczność kosztów na żywo w podziale na umowę konserwacyjną",
      body: "Done-it daje zespołom konserwacyjnym dowód obecności potwierdzony przez GPS w podziale na umowę, niestandardowe rodzaje nieobecności i widok kosztów na żywo dla każdego cyklicznego klienta — spełniając każdy lokalny wymóg rejestracji czasu pracy bez dodatkowego sprzętu.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Pracownik przybywa na lokalizację konserwacyjną", body: "Zameldowanie z weryfikacją lokalizacji rejestruje czas przybycia i wiąże godziny z właściwą umową. Dowód obecności tworzony jest automatycznie." },
      { step: "Zadania ukończone, problemy zarejestrowane", body: "Ukończona praca i wszelkie problemy są zgłaszane z aplikacji ze zdjęciami. Klienci mogą dokładnie zobaczyć, co zostało zrobione." },
      { step: "Koszt w podziale na umowę widoczny natychmiast", body: "Godziny kumulują się względem budżetu umowy w czasie rzeczywistym. Znasz marżę, zanim umowa zostanie odnowiona." },
    ],
    stats: [
      { value: "Na żywo", label: "koszt w podziale na umowę konserwacyjną" },
      { value: "GPS", label: "dowód obecności gotowy dla klientów na żądanie" },
      { value: "0", label: "odnowień umów bez znajomości swojej marży" },
    ],
    quote: {
      text: "Możemy powiedzieć zarządcy obiektu dokładnie, kto był na lokalizacji i kiedy, w dwie minuty. Ten poziom dowodu zmienił sposób, w jaki klienci nas postrzegają.",
      who: "Kierownik operacyjny, firma konserwacyjna · 8 pracowników",
      result: "Spory z klientami zredukowane do zera",
    },
    relatedSlugs: ["cleaning", "landscaping", "field-service"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },
  {
    slug: "retail",
    name: "Sklepy i handel",
    headline: "Ewidencja czasu pracy w sklepie — rejestracja zmiany, zastępstwa i eksport do płac w jednej aplikacji",
    intro: "Zmiany poranne, zmiany zamykające i ciągłe zastępstwa. Done-it daje każdemu sklepowi czysty, cyfrowy zapis tego, kto pracował na której zmianie — zarejestrowany z własnego telefonu, gotowy do naliczenia płac na koniec miesiąca.",
    pains: [
      {
        title: "Zamiany zmian nigdy nie docierają do płac",
        body: "Ktoś zastępuje chorego kolegę, ktoś inny zostaje godzinę dłużej przy zamykaniu. Zapisane na karteczce albo wcale — i lista płac jest błędna, zanim się zacznie.",
      },
      {
        title: "Kilka sklepów, jedno naliczanie płac",
        body: "Ludzie przechodzą między filiami, pracownicy na część etatu obsługują dwie lokalizacje. Zbieranie godzin z każdego sklepu i ręczne scalanie zabiera dzień w miesiącu.",
      },
      {
        title: "Przerwy i nadgodziny liczone na oko",
        body: "Przerwy ustawowe odliczane z pamięci, dodatkowe godziny rozliczane nieformalnie — czyli dokładnie to, o co kontrola pyta w pierwszej kolejności.",
      },
    ],
    features: [
      {
        title: "Rejestracja wejścia na sali sprzedaży",
        body: "Jedno tapnięcie na własnym telefonie na początku zmiany. Bez terminala przy kasie, bez wspólnej karty, bez listy na magazynie.",
      },
      {
        title: "Godziny na sklep, ludzie między filiami",
        body: "Każdy sklep to osobny projekt. Kto zastępuje w innej filii, rejestruje godziny na tę filię — widzisz je tam, gdzie faktycznie zostały przepracowane.",
      },
      {
        title: "Przerwy odliczane automatycznie",
        body: "Ustawiasz regułę przerw raz i obowiązuje na każdej zmianie. Nadgodziny zapisują się w momencie powstania, a każdy pracownik wybiera wypłatę albo odbiór.",
      },
    ],
    compliance: {
      label: "Ewidencja pracy zmianowej",
      title: "Zapis każdej przepracowanej zmiany, który się obroni",
      body: "Done-it prowadzi obiektywny, cyfrowy zapis każdego wejścia i wyjścia, wraz z przerwami i nadgodzinami — dokładnie taki, jakiego wymaga kontrola albo spór o wynagrodzenie.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Pracownik rejestruje wejście na zmianę", body: "Jedno tapnięcie na telefonie na starcie zmiany. Zastępstwo w innej filii zapisuje się na tę filię." },
      { step: "Widzisz, kto jest w pracy, w każdym sklepie", body: "Podgląd na żywo we wszystkich lokalizacjach — nieobsadzoną zmianę poranną zauważysz, zanim otworzysz drzwi." },
      { step: "Płace w jednym eksporcie", body: "Godziny, przerwy i nadgodziny na pracownika i na sklep, wyeksportowane w formie gotowej dla biura rachunkowego." },
    ],
    stats: [
      { value: "1 tapnięcie", label: "aby zarejestrować wejście — bez terminala przy kasie" },
      { value: "Na żywo", label: "podgląd, kto pracuje, we wszystkich sklepach" },
      { value: "0", label: "zamian zmian zgubionych między sklepem a płacami" },
    ],
    quote: {
      text: "Dwa sklepy i sporo osób na część etatu. Wcześniej połowę zastępstw rozliczaliśmy z pamięci. Teraz każda godzina trafia na właściwy sklep, a płace zajmują minuty.",
      who: "Właściciel, handel detaliczny · 11 pracowników",
      result: "Przygotowanie płac z całego dnia do niecałej godziny",
    },
    relatedSlugs: ["hospitality", "beauty", "offices"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "offices",
    name: "Biura i usługi profesjonalne",
    headline: "Ewidencja czasu pracy w biurze — godziny na klienta i projekt, bez arkusza kalkulacyjnego",
    intro: "Biura rachunkowe, kancelarie notarialne, pracownie architektoniczne, agencje i firmy doradcze fakturują swój czas — albo przynajmniej muszą wiedzieć, gdzie zniknął. Done-it zapisuje godziny na właściwego klienta i projekt, zza biurka i w terenie.",
    pains: [
      {
        title: "Godziny do faktury odtwarzane w piątek",
        body: "Czas spisany na koniec tygodnia to czas zgadnięty. Całe godziny po cichu znikają między pracą a fakturą.",
      },
      {
        title: "Nie wiadomo, ile naprawdę kosztuje klient",
        body: "Stawkę ustalono kilka miesięcy temu. Czy praca wciąż się w niej mieści, okazuje się dopiero, gdy ktoś to podliczy — zwykle za późno.",
      },
      {
        title: "Praca hybrydowa dziurawi ewidencję",
        body: "Część zespołu w biurze, część w domu, część u klienta. Obecność przestaje być faktem, a staje się rekonstrukcją.",
      },
    ],
    features: [
      {
        title: "Godziny na klienta i projekt",
        body: "Każdy wpis trafia do projektu, więc widzisz godziny na klienta bez eksportowania czegokolwiek i bez pytania kogokolwiek.",
      },
      {
        title: "Działa przy biurku i poza nim",
        body: "Ta sama aplikacja w telefonie i w przeglądarce. Wizyta u klienta liczy się jako praca dla tego klienta, razem z dojazdem, jeśli tak chcesz.",
      },
      {
        title: "Nadgodziny i urlopy w jednym miejscu",
        body: "Dodatkowe godziny zapisywane na bieżąco, urlop zgłaszany i zatwierdzany w aplikacji — bez równoległego arkusza, kto i kiedy jest wolny.",
      },
    ],
    compliance: {
      label: "Ewidencja czasu na klienta",
      title: "Obiektywny zapis przepracowanych godzin",
      body: "Done-it prowadzi cyfrową, odporną na manipulacje ewidencję czasu pracy każdego pracownika — podstawę zarówno dla płac, jak i dla faktury, którą obronisz przed klientem.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Zakładasz projekt klienta", body: "Konfigurujesz go raz — z klientem i, jeśli rozliczasz się z budżetu, z zaplanowaną liczbą godzin." },
      { step: "Zespół zapisuje na nim czas", body: "Zza biurka albo z telefonu. Każda godzina trafia do klienta, a nie do czyjejś pamięci." },
      { step: "Widzisz godziny na klienta, eksportujesz do płac", body: "Sumy na projekt na żywo i czysty eksport do płac albo do fakturowania na koniec miesiąca." },
    ],
    stats: [
      { value: "Na klienta", label: "godziny widoczne, gdy praca jeszcze trwa" },
      { value: "Biuro i teren", label: "ta sama ewidencja, telefon i przeglądarka" },
      { value: "1 eksport", label: "do płac i do fakturowania" },
    ],
    quote: {
      text: "Kiedyś w każdy piątek odtwarzaliśmy cały tydzień. Teraz godziny są już przy właściwym kliencie i widzę, które sprawy zjadają honorarium.",
      who: "Wspólnik, biuro rachunkowe · 8 pracowników",
      result: "Odzyskane godziny do zafakturowania co miesiąc",
    },
    relatedSlugs: ["retail", "healthcare", "wellness"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "beauty",
    name: "Salony i pielęgnacja ciała",
    headline: "Ewidencja czasu pracy w salonie — zmiany, praca na część etatu i płace bez papierowego kalendarza",
    intro: "Fryzjerzy, barberzy, studia paznokci i gabinety kosmetyczne działają na zmianach i umowach na część etatu. Done-it zapisuje, kto kiedy pracował, automatycznie odlicza przerwy i przekazuje do płac czysty plik.",
    pains: [
      {
        title: "Praca na część etatu, niekończące się warianty",
        body: "Trzy dni w jednym tygodniu, cztery w kolejnym, sobota zamieniona na wtorek. Godziny z umowy i godziny faktyczne szybko się rozjeżdżają.",
      },
      {
        title: "Kalendarz wizyt to nie ewidencja czasu pracy",
        body: "Rezerwacje mówią, co było umówione, a nie kto pracował. Gdy pyta księgowość albo kontrola, system rezerwacji jest złą odpowiedzią.",
      },
      {
        title: "Dodatkowe godziny rozliczane nieformalnie",
        body: "Zostanie po godzinach dla ostatniego klienta jest zapamiętane, obiecane i czasem zapomniane. Z takiej luki rodzi się spór.",
      },
    ],
    features: [
      {
        title: "Rejestracja wejścia w salonie",
        body: "Jedno tapnięcie na własnym telefonie na początku zmiany — bez wspólnego terminala w recepcji, bez listy obecności przy lustrze.",
      },
      {
        title: "Przerwy i zasady niepełnego etatu raz",
        body: "Regułę przerw i wymiar godzin ustawiasz raz; obowiązują na każdej zmianie, z wyjątkami dla osób, których umowa mówi co innego.",
      },
      {
        title: "Nadgodziny: wypłata albo odbiór",
        body: "Dodatkowe godziny zapisują się w trakcie pracy, a każdy pracownik wybiera wypłatę albo czas wolny — rozliczane w aplikacji, nie z pamięci.",
      },
    ],
    compliance: {
      label: "Cyfrowa ewidencja czasu pracy",
      title: "Zapis, który znaczy więcej niż kalendarz wizyt",
      body: "Done-it rejestruje faktyczny czas pracy każdej osoby, z przerwami i nadgodzinami — niezależnie od rezerwacji i wiarygodnie, gdy przychodzi kontrola.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Pracownik rejestruje wejście na zmianę", body: "Jedno tapnięcie po przyjściu. Przerwy odliczane według reguły, którą ustawiasz raz." },
      { step: "Widzisz dzień na bieżąco", body: "Kto pracuje, kto jest na przerwie, kto został dłużej — na żywo, bez pytania." },
      { step: "Płace dostają czysty plik", body: "Przepracowane godziny, przerwy i nadgodziny na pracownika, wyeksportowane dla biura rachunkowego." },
    ],
    stats: [
      { value: "1 tapnięcie", label: "aby zarejestrować wejście — nic wspólnego, nic na papierze" },
      { value: "Auto", label: "odliczanie przerw na każdej zmianie" },
      { value: "0", label: "godzin po zamknięciu rozliczanych z pamięci" },
    ],
    quote: {
      text: "Wszyscy są na część etatu i każdy tydzień wygląda inaczej. Aplikacja po prostu to zapisuje — przestałam odtwarzać cały miesiąc w arkuszu.",
      who: "Właścicielka, salon fryzjerski · 6 pracowników",
      result: "Koniec sporów o godziny na koniec miesiąca",
    },
    relatedSlugs: ["wellness", "retail", "hospitality"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "wellness",
    name: "Zdrowie i wellness",
    headline: "Ewidencja czasu pracy dla siłowni, studiów i spa — zmiany, zajęcia i płace w jednej aplikacji",
    intro: "Siłownie, studia fitness, gabinety fizjoterapii i spa pracują w długich godzinach otwarcia z rotującą obsadą. Done-it zapisuje, kto obsłużył którą zmianę, automatycznie odlicza przerwy i czysto eksportuje dane do płac.",
    pains: [
      {
        title: "Długie godziny otwarcia, rotujące zastępstwa",
        body: "Poranne zmiany, wieczorne zajęcia i weekendy, obsadzane mieszanką etatów, części etatu i współpracowników. Grafik to plan, a nie ewidencja.",
      },
      {
        title: "Godziny zajęć i godziny na sali się zlewają",
        body: "Instruktor prowadzi dwa treningi, a w przerwie zastępuje w recepcji. Żeby zapłacić poprawnie, trzeba wiedzieć, co faktycznie przepracował, a nie co było w grafiku.",
      },
      {
        title: "Kilka lokalizacji, jedna administracja",
        body: "Ludzie przechodzą między studiami. Ręczne scalanie ich godzin co miesiąc jest wolne i podatne na błędy.",
      },
    ],
    features: [
      {
        title: "Rejestracja wejścia w każdym studiu",
        body: "Jedno tapnięcie na telefonie w obiekcie, w którym pracują. Zastępstwo w innym studiu trafia na godziny tego studia.",
      },
      {
        title: "Podgląd na żywo, kto jest w pracy",
        body: "Widzisz salę we wszystkich lokalizacjach w czasie rzeczywistym — nieobsadzoną poranną zmianę zauważysz przed otwarciem, a nie po.",
      },
      {
        title: "Przerwy i nadgodziny pod kontrolą",
        body: "Reguły przerw stosowane automatycznie, dodatkowe godziny zapisywane na bieżąco, z wyborem wypłaty albo odbioru dla każdego pracownika.",
      },
    ],
    compliance: {
      label: "Ewidencja zmian w wielu obiektach",
      title: "Obiektywny zapis każdej zmiany i każdego obiektu",
      body: "Done-it prowadzi cyfrową ewidencję czasu pracy na pracownika i na lokalizację — podstawę poprawnego wynagrodzenia i każdej kontroli przepracowanych godzin.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Pracownik rejestruje wejście w studiu", body: "Jedno tapnięcie na początku zmiany, w obiekcie, w którym pracuje." },
      { step: "Zastępstwa widać na żywo", body: "Kto pracuje i w której lokalizacji w tej chwili, więc luki zapełniasz, zanim zauważą je klienci." },
      { step: "Jeden eksport do płac", body: "Godziny, przerwy i nadgodziny na pracownika i na studio, gotowe dla biura rachunkowego." },
    ],
    stats: [
      { value: "Na studio", label: "godziny, nawet gdy ktoś zastępuje gdzie indziej" },
      { value: "Na żywo", label: "podgląd sali we wszystkich lokalizacjach" },
      { value: "Auto", label: "przerwy i nadgodziny, bez ręcznych obliczeń" },
    ],
    quote: {
      text: "Dwa studia, długie godziny otwarcia i mnóstwo zastępstw. Teraz godziny trafiają do właściwego obiektu, a płace to jeden plik.",
      who: "Menedżer, studio fitness · 14 pracowników",
      result: "Grafik i płace wreszcie się zgadzają",
    },
    relatedSlugs: ["beauty", "healthcare", "hospitality"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "hospitality",
    name: "Gastronomia i hotelarstwo",
    headline: "Ewidencja czasu pracy w kawiarniach, restauracjach i hotelach — zmiany, przerwy i nadgodziny rozliczone uczciwie",
    intro: "Zmiany dzielone, późne zamknięcia i sobota, która się przeciąga. Done-it zapisuje godziny tak, jak zostały faktycznie przepracowane, odlicza przerwy według twojej reguły i przekazuje do płac jeden czysty plik.",
    pains: [
      {
        title: "Zmiana nigdy nie kończy się wtedy, co w grafiku",
        body: "Serwis się przeciąga, zamykanie zajmuje kolejne pół godziny. To realna praca, a rozliczanie jej z pamięci to początek sporów.",
      },
      {
        title: "Zmiany dzielone trudno zapisać",
        body: "Serwis lunchowy, przerwa, potem kolacje. Na papierze robi się z tego jeden długi blok albo dwa domysły — i jedno, i drugie mija się z prawdą.",
      },
      {
        title: "Duża rotacja, ciągłe wdrażanie",
        body: "Nowi ludzie w każdym sezonie. System, który wymaga szkolenia, nie przetrwa pierwszego gorącego tygodnia.",
      },
    ],
    features: [
      {
        title: "Wejście i wyjście przy każdym serwisie",
        body: "Jedno tapnięcie na początku i na końcu każdego bloku, więc zmiany dzielone są zapisane tak, jak przebiegły — a nie scalone w jedną.",
      },
      {
        title: "Przerwy według twojej reguły",
        body: "Obowiązkowe przerwy odliczane automatycznie, z wyjątkami dla osób, których umowa tego wymaga. Bez ręcznych obliczeń na koniec miesiąca.",
      },
      {
        title: "Nadgodziny rozliczane w aplikacji",
        body: "Dodatkowe pół godziny po zamknięciu zapisuje się w momencie pracy i zostaje wypłacone albo odebrane jako czas wolny — wybiera pracownik.",
      },
    ],
    compliance: {
      label: "Ewidencja zmian dzielonych",
      title: "Wiarygodny zapis godzin w zespole o dużej rotacji",
      body: "Done-it prowadzi obiektywny, cyfrowy zapis każdej zmiany, przerwy i dodatkowej godziny — dokładnie to, o co pyta kontrola płac albo inspekcja pracy.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Pracownik rejestruje wejście na serwis", body: "Jedno tapnięcie na początku bloku i tak samo na końcu. Zmiany dzielone pozostają dzielone." },
      { step: "Widzisz salę na żywo", body: "Kto pracuje na dzisiejszym serwisie, kto jest jeszcze na przerwie, kto został po zamknięciu." },
      { step: "Płace dostają realne godziny", body: "Czas pracy, przerwy i nadgodziny na pracownika, wyeksportowane w jednym pliku." },
    ],
    stats: [
      { value: "Zmiany dzielone", label: "zapisane jako dwa bloki, a nie jeden domysł" },
      { value: "Auto", label: "odliczanie przerw według twojej reguły" },
      { value: "1 min", label: "na wdrożenie nowej osoby — to jej własny telefon" },
    ],
    quote: {
      text: "Zamykanie zawsze się przeciąga. Teraz te minuty są zapisane zamiast być tematem kłótni, a ludzie mogą je odebrać jako czas wolny.",
      who: "Właściciel, restauracja · 17 pracowników",
      result: "Mniej sporów, szybsze płace",
    },
    relatedSlugs: ["retail", "wellness", "beauty"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "healthcare",
    name: "Ochrona zdrowia i opieka",
    headline: "Ewidencja czasu pracy dla zespołów opiekuńczych — zmiany, wizyty domowe i kwalifikacje w jednym zapisie",
    intro: "Przychodnie, zespoły opieki domowej i domy opieki działają wokół zmian, wizyt i rygorystycznej dokumentacji. Done-it rejestruje czas pracy na zmianę i na wizytę oraz przechowuje dokumenty pracowników tam, gdzie je znajdziesz.",
    pains: [
      {
        title: "Wizyty i zmiany w osobnych systemach",
        body: "Wizyty domowe w jednym narzędziu, godziny z grafiku w drugim, a płace dostają trzecią wersję. Uzgadnianie tego to comiesięczna praca.",
      },
      {
        title: "Godziny nocne i weekendowe wyceniane błędnie",
        body: "Praca w porze nocnej i w weekendy ma inne stawki. Gdy ewidencja jest przybliżona, wynagrodzenie też — a korekta przychodzi później.",
      },
      {
        title: "Certyfikaty wygasają po cichu",
        body: "W opiece kwalifikacje i certyfikaty mają znaczenie, a leżą w segregatorze, którego nikt nie otwiera do czasu kontroli.",
      },
    ],
    features: [
      {
        title: "Rejestracja na zmianę albo na wizytę",
        body: "Jedno tapnięcie na początku zmiany albo pod każdym adresem klienta w opiece domowej — z weryfikacją lokalizacji tam, gdzie potrzebujesz dowodu.",
      },
      {
        title: "Godziny podzielone tak, jak je płacisz",
        body: "Godziny nocne, weekendowe i zwykłe zapisywane osobno, więc eksport do płac od razu odzwierciedla sposób rozliczania pracy.",
      },
      {
        title: "Dokumenty pracowników w jednym miejscu",
        body: "Umowy, certyfikaty i dokumenty tożsamości przechowywane przy osobie, której dotyczą — odnajdziesz je w kilka sekund, gdy ktoś o nie poprosi.",
      },
    ],
    compliance: {
      label: "Ewidencja zmian i wizyt",
      title: "Dokumentacja, która przechodzi kontrolę",
      body: "Done-it prowadzi cyfrowy, obiektywny zapis czasu pracy każdej osoby — godziny, przerwy i podział na godziny nocne i weekendowe — razem z dokumentami przypisanymi do pracownika.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Rejestracja wejścia na zmianę lub wizytę", body: "Jedno tapnięcie w placówce albo pod adresem klienta, z weryfikacją lokalizacji tam, gdzie potrzebny jest dowód." },
      { step: "Godziny trafiają do właściwej kategorii", body: "Godziny zwykłe, nocne i weekendowe zapisywane osobno, na bieżąco." },
      { step: "Eksport do płac", body: "Jeden czysty plik za okres, już podzielony tak, jak rozliczasz wynagrodzenia." },
    ],
    stats: [
      { value: "Na wizytę", label: "albo na zmianę — jak pracuje twój zespół" },
      { value: "Osobno", label: "godziny nocne i weekendowe, zapisane jak przepracowane" },
      { value: "W aktach", label: "certyfikaty przechowywane przy każdym pracowniku" },
    ],
    quote: {
      text: "Wizyty domowe i zmiany z grafiku wreszcie są w jednym zapisie. Płace przestały być ćwiczeniem z uzgadniania danych.",
      who: "Koordynatorka, zespół opieki domowej · 22 pracowników",
      result: "Jeden zapis zamiast trzech",
    },
    relatedSlugs: ["wellness", "offices", "cleaning"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },
];
